"use strict";
/**
 * Applies a `/app/*` URL to the app (src/shared/app-routes.ts).
 *
 * - A URL opened while signed out is held, in memory only, and applied once
 *   the user signs in. The tap pad is bookmarked in the stadium; landing on the
 *   events page after every login would cost the operator the pad mid-drive.
 * - Of the address's query, only the NBA pad's validated `home`, `away` (A191) and `pad` (1 or 2,
 *   N8 §19.5) survive: the login screen rewrites the address, so they are kept with the held route.
 * - The win-prob pages are owner-only. A tier without the feature is sent to
 *   the default page. The /win-prob API refuses it regardless; this keeps the
 *   page from opening empty.
 *
 * It does not subscribe to state itself: the app keeps exactly one state
 * subscription (the startup test pins it), and calls `onState` from it.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.allowedRouteQuery = allowedRouteQuery;
exports.createRouteApplier = createRouteApplier;
const TEAM = /^[A-Za-z0-9]{1,5}$/;
/** The NBA pad's game parameters from `search`, validated; nothing else, and nothing elsewhere. */
function allowedRouteQuery(segments, search) {
    if (segments[0] !== "nba" || segments.length !== 3 || segments[2] !== "tap")
        return "";
    const q = new URLSearchParams(search);
    const out = new URLSearchParams();
    for (const k of ["home", "away"]) {
        const v = q.get(k);
        if (v && TEAM.test(v))
            out.set(k, v.toUpperCase());
    }
    const pad = q.get("pad");
    if (pad === "1" || pad === "2")
        out.set("pad", pad);
    return out.toString();
}
function createRouteApplier(deps) {
    let pending = null;
    const getSearch = deps.getSearch ?? (() => "");
    const apply = (page, segments, query) => {
        if (page === "win-prob") {
            if (deps.canWinProb())
                deps.openWinProb(segments, query);
            else
                deps.navigate(null);
            return;
        }
        deps.navigate(page);
    };
    return {
        apply: (page, segments) => {
            const query = page === "win-prob" ? allowedRouteQuery(segments, getSearch()) : "";
            if (deps.getState().currentPage === "login") {
                pending = page ? { page, segments, query } : null;
                return;
            }
            apply(page, segments, query);
        },
        onState: (state) => {
            if (!pending || !state.isAuthenticated || state.currentPage === "login")
                return;
            const route = pending;
            pending = null; // before applying: applying changes state and re-enters here
            apply(route.page, route.segments, route.query);
        },
    };
}
